import { createTheme, alpha } from '@mui/material/styles';

// ─── Design tokens ─────────────────────────────────────────────────────────
// Ported from MUI's own Dashboard template source (verified against
// mui/material-ui on GitHub — docs/data/material/getting-started/templates/
// shared-theme/themePrimitives.ts and customizations/*.tsx). Color scales,
// typography scale, shape and the component overrides below that are
// annotated "sourced" match that file's literal values. A few gaps in that
// file (chip/alert color variants beyond default/success/error/warning, a
// `secondary` palette color — the template doesn't define one) are filled in
// as clearly-marked extensions, following the same pattern the sourced parts
// use, so the app doesn't render bare/inconsistent for cases the template
// itself doesn't cover.

const pxToRem = (px) => `${px / 16}rem`;

const brand = {
  50: 'hsl(210, 100%, 95%)',
  100: 'hsl(210, 100%, 92%)',
  200: 'hsl(210, 100%, 80%)',
  300: 'hsl(210, 100%, 65%)',
  400: 'hsl(210, 98%, 48%)',
  500: 'hsl(210, 98%, 42%)',
  600: 'hsl(210, 98%, 55%)',
  700: 'hsl(210, 100%, 35%)',
  800: 'hsl(210, 100%, 16%)',
  900: 'hsl(210, 100%, 21%)',
};

const gray = {
  50: 'hsl(220, 35%, 97%)',
  100: 'hsl(220, 30%, 94%)',
  200: 'hsl(220, 20%, 88%)',
  300: 'hsl(220, 20%, 80%)',
  400: 'hsl(220, 20%, 65%)',
  500: 'hsl(220, 20%, 42%)',
  600: 'hsl(220, 20%, 35%)',
  700: 'hsl(220, 20%, 25%)',
  800: 'hsl(220, 30%, 6%)',
  900: 'hsl(220, 35%, 3%)',
};

const green = {
  50: 'hsl(120, 80%, 98%)',
  200: 'hsl(120, 75%, 87%)',
  300: 'hsl(120, 61%, 77%)',
  500: 'hsl(120, 59%, 30%)',
  800: 'hsl(120, 84%, 10%)',
  900: 'hsl(120, 87%, 6%)',
};

const orange = {
  100: 'hsl(45, 92%, 90%)',
  300: 'hsl(45, 90%, 65%)',
  500: 'hsl(45, 90%, 35%)',
  800: 'hsl(45, 95%, 16%)',
  900: 'hsl(45, 93%, 12%)',
};

const red = {
  50: 'hsl(0, 100%, 97%)',
  100: 'hsl(0, 92%, 90%)',
  200: 'hsl(0, 94%, 80%)',
  300: 'hsl(0, 90%, 65%)',
  500: 'hsl(0, 90%, 30%)',
  700: 'hsl(0, 94%, 18%)',
  800: 'hsl(0, 95%, 12%)',
  900: 'hsl(0, 93%, 6%)',
};

// Elevated-surface shadow (Card/Menu) — sourced.
const surfaceShadow = {
  light: 'hsla(220, 30%, 5%, 0.07) 0px 4px 16px 0px, hsla(220, 25%, 10%, 0.07) 0px 8px 16px -5px',
  dark: 'hsla(220, 30%, 5%, 0.7) 0px 4px 16px 0px, hsla(220, 25%, 10%, 0.8) 0px 8px 16px -5px',
};

// ─── Typography (Arabic-capable when RTL; RTL font stack is Invotrix's own
// addition — the template only targets Latin) ──────────────────────────────
const buildTypography = (direction) => ({
  fontFamily: direction === 'rtl'
    ? '"Tajawal", "Inter", "Roboto", "Helvetica Neue", Arial, sans-serif'
    : '"Inter", "Roboto", "Helvetica Neue", Arial, sans-serif',
  h1: { fontSize: pxToRem(48), fontWeight: 600, lineHeight: 1.2 },
  h2: { fontSize: pxToRem(36), fontWeight: 600, lineHeight: 1.2 },
  h3: { fontSize: pxToRem(30), lineHeight: 1.2 },
  h4: { fontSize: pxToRem(24), fontWeight: 600, lineHeight: 1.5 },
  h5: { fontSize: pxToRem(20), fontWeight: 600 },
  h6: { fontSize: pxToRem(18), fontWeight: 600 },
  subtitle1: { fontSize: pxToRem(18) },
  subtitle2: { fontSize: pxToRem(14), fontWeight: 500 },
  body1: { fontSize: pxToRem(14) },
  body2: { fontSize: pxToRem(14), fontWeight: 400 },
  caption: { fontSize: pxToRem(12), fontWeight: 400 },
  button: { textTransform: 'none', fontWeight: 600 },
});

/**
 * Creates the Invotrix MUI theme.
 * @param {'light'|'dark'} mode
 * @param {'ltr'|'rtl'}    direction
 */
export const createAppTheme = (mode = 'light', direction = 'ltr') => {
  const isLight = mode === 'light';
  const shadow = isLight ? surfaceShadow.light : surfaceShadow.dark;
  const dividerColor = isLight ? alpha(gray[300], 0.4) : alpha(gray[700], 0.6);
  const hoverColor = isLight ? alpha(gray[200], 0.2) : alpha(gray[600], 0.2);
  const selectedColor = isLight ? alpha(gray[200], 0.3) : alpha(gray[600], 0.3);

  return createTheme({
    direction,
    palette: {
      mode,
      primary: {
        main: brand[400],
        light: isLight ? brand[200] : brand[300],
        dark: brand[700],
        contrastText: brand[50],
      },
      // Extension: the template doesn't define a `secondary` color at all.
      // Reusing its own neutral gray scale keeps anything using color="secondary"
      // (a handful of Chips) visually consistent with the rest of the palette
      // instead of falling back to MUI's default purple.
      secondary: {
        main: isLight ? gray[700] : gray[300],
        contrastText: isLight ? '#ffffff' : gray[900],
      },
      background: {
        default: isLight ? 'hsl(0, 0%, 99%)' : gray[900],
        paper: isLight ? gray[50] : gray[800],
      },
      text: {
        primary: isLight ? 'hsl(220, 30%, 6%)' : 'hsl(0, 0%, 100%)',
        secondary: isLight ? gray[600] : gray[400],
      },
      divider: dividerColor,
      action: { hover: hoverColor, selected: selectedColor },
      success: { main: isLight ? 'hsl(120, 44%, 53%)' : green[500] },
      warning: { main: isLight ? 'hsl(45, 90%, 40%)' : orange[500] },
      error: { main: isLight ? 'hsl(0, 90%, 40%)' : red[500] },
      info: { main: isLight ? brand[300] : brand[700] },
    },
    typography: buildTypography(direction),
    shape: { borderRadius: 8 },
    components: {
      // ── Buttons: monochrome "contained" look — sourced. This is the
      // template's signature choice: brand blue is reserved for links, focus
      // rings and accents, not filled action buttons.
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            textTransform: 'none',
            fontWeight: 600,
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' },
          },
          contained: isLight
            ? {
                color: '#ffffff',
                backgroundColor: gray[900],
                backgroundImage: `linear-gradient(to bottom, ${gray[700]}, ${gray[800]})`,
                border: `1px solid ${gray[700]}`,
                '&:hover': { backgroundColor: gray[700], boxShadow: 'none' },
                '&:focus-visible': { outline: `3px solid ${alpha(brand[400], 0.5)}` },
              }
            : {
                color: '#000000',
                backgroundColor: gray[50],
                backgroundImage: `linear-gradient(to bottom, ${gray[100]}, ${gray[50]})`,
                // Not given explicitly in the source extraction — a mid-tone
                // border that reads against the light gradient on a dark page.
                border: `1px solid ${gray[600]}`,
                '&:hover': { backgroundColor: gray[300] },
                '&:active': { backgroundColor: gray[400] },
                '&:focus-visible': { outline: `3px solid ${alpha(brand[400], 0.5)}` },
              },
        },
      },
      // ── Inputs — sourced.
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            backgroundColor: isLight ? 'hsl(0, 0%, 99%)' : gray[900],
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: isLight ? gray[400] : gray[500],
            },
            '&.Mui-focused': {
              outline: `3px solid ${alpha(brand[500], 0.5)}`,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: brand[400],
            },
          },
          // Native date/time pickers draw their own icon; without this it stays
          // dark-on-dark in dark mode. Applies to every <TextField type="date">.
          input: {
            colorScheme: mode,
          },
          notchedOutline: {
            borderColor: dividerColor,
          },
        },
      },
      // ── Surfaces — sourced (flat, bordered, no shadow).
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            backgroundImage: 'none',
            backgroundColor: isLight ? gray[50] : gray[800],
            border: `1px solid ${dividerColor}`,
            boxShadow: 'none',
          },
        },
      },
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: { backgroundImage: 'none', boxShadow: 'none' },
        },
      },
      // ── Chips — pill shape + fontWeight sourced; the info/primary/
      // secondary color variants are Invotrix's extension, mechanically
      // following the same border/bg/text pattern the sourced default/
      // success/error variants use.
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: 999, border: '1px solid', fontWeight: 600 },
        },
        variants: [
          {
            props: { color: 'default' },
            style: {
              borderColor: isLight ? gray[200] : gray[700],
              backgroundColor: isLight ? gray[100] : gray[800],
              color: isLight ? gray[500] : gray[300],
            },
          },
          {
            props: { color: 'success' },
            style: {
              borderColor: isLight ? green[200] : green[800],
              backgroundColor: isLight ? green[50] : green[900],
              color: isLight ? green[500] : green[300],
            },
          },
          {
            props: { color: 'error' },
            style: {
              borderColor: isLight ? red[100] : red[800],
              backgroundColor: isLight ? red[50] : red[900],
              color: isLight ? red[500] : red[200],
            },
          },
          {
            props: { color: 'warning' },
            style: {
              borderColor: isLight ? alpha(orange[300], 0.5) : alpha(orange[800], 0.5),
              backgroundColor: isLight ? orange[100] : alpha(orange[900], 0.5),
              color: isLight ? orange[500] : orange[300],
            },
          },
          {
            props: { color: 'info' },
            style: {
              borderColor: isLight ? brand[200] : brand[700],
              backgroundColor: isLight ? brand[50] : brand[900],
              color: isLight ? brand[500] : brand[300],
            },
          },
          {
            props: { color: 'primary' },
            style: {
              borderColor: isLight ? brand[200] : brand[700],
              backgroundColor: isLight ? brand[50] : brand[900],
              color: isLight ? brand[500] : brand[300],
            },
          },
          {
            props: { color: 'secondary' },
            style: {
              borderColor: isLight ? gray[300] : gray[600],
              backgroundColor: isLight ? gray[100] : gray[700],
              color: isLight ? gray[700] : gray[200],
            },
          },
        ],
      },
      // ── Feedback — warning sourced; success/error/info are Invotrix's
      // extension, same mechanical pattern.
      MuiAlert: {
        styleOverrides: { root: { borderRadius: 10 } },
        variants: [
          {
            props: { severity: 'warning' },
            style: {
              backgroundColor: isLight ? orange[100] : alpha(orange[900], 0.5),
              border: `1px solid ${isLight ? alpha(orange[300], 0.5) : alpha(orange[800], 0.5)}`,
              '& .MuiAlert-icon': { color: orange[500] },
            },
          },
          {
            props: { severity: 'error' },
            style: {
              backgroundColor: isLight ? red[50] : alpha(red[900], 0.5),
              border: `1px solid ${isLight ? alpha(red[300], 0.5) : alpha(red[800], 0.5)}`,
              '& .MuiAlert-icon': { color: red[500] },
            },
          },
          {
            props: { severity: 'success' },
            style: {
              backgroundColor: isLight ? green[50] : alpha(green[900], 0.5),
              border: `1px solid ${isLight ? alpha(green[300], 0.5) : alpha(green[800], 0.5)}`,
              '& .MuiAlert-icon': { color: green[500] },
            },
          },
          {
            props: { severity: 'info' },
            style: {
              backgroundColor: isLight ? brand[50] : alpha(brand[900], 0.5),
              border: `1px solid ${isLight ? alpha(brand[300], 0.5) : alpha(brand[800], 0.5)}`,
              '& .MuiAlert-icon': { color: brand[500] },
            },
          },
        ],
      },
      MuiDialog: {
        styleOverrides: {
          paper: { borderRadius: 10, border: `1px solid ${dividerColor}` },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            height: 8,
            borderRadius: 8,
            backgroundColor: isLight ? gray[200] : gray[800],
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: { borderRadius: 8, padding: '6px 8px' },
        },
        },
      MuiButton: {
            styleOverrides: {
                root: {
                    '&.Mui-disabled': {
                        backgroundColor: '#4a4a4a !important',
                        color: '#ffffff !important',
                    }
                }
            }
        },
      MuiMenu: {
        styleOverrides: {
          paper: {
            marginTop: 4,
            borderRadius: 8,
            border: `1px solid ${dividerColor}`,
            backgroundColor: isLight ? '#ffffff' : gray[900],
            boxShadow: shadow,
          },
          list: { gap: 0 },
        },
      },
      MuiDivider: {
        styleOverrides: { root: { borderColor: dividerColor } },
      },
      MuiTableRow: {
        styleOverrides: {
          root: { '&:hover': { backgroundColor: hoverColor } },
        },
      },
      // ── Navigation chrome — Invotrix's Sidebar/Header set their own inline
      // colors (see components/layout/Sidebar.jsx, Header.jsx) to match this
      // theme's neutral, flat nav design; these are just safe fallbacks.
      MuiDrawer: {
        styleOverrides: {
          paper: { backgroundColor: isLight ? 'hsl(0, 0%, 99%)' : gray[900] },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: isLight ? gray[50] : gray[800],
            color: isLight ? 'hsl(220, 30%, 6%)' : 'hsl(0, 0%, 100%)',
            boxShadow: 'none',
            borderBottom: `1px solid ${dividerColor}`,
          },
        },
      },
    },
  });
};

// Convenience named exports (LTR) for files not yet updated
export const lightTheme = createAppTheme('light', 'ltr');
export const darkTheme = createAppTheme('dark', 'ltr');
